<template>
  <div class="shipment-list-view">
    <div class="page-header">
      <h2>선적 스케줄 관리</h2>
      <div class="header-actions">
        <el-button
          v-if="userStore.isLCManager"
          type="primary"
          @click="openCreateDialog"
        >
          <el-icon><Plus /></el-icon>
          신규 선적
        </el-button>
        <el-button @click="showHelp">
          <el-icon><QuestionFilled /></el-icon>
          도움말
        </el-button>
      </div>
    </div>

    <!-- 필터 영역 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters">
        <el-form-item label="상태">
          <!-- width 미지정 시 el-select 가 접혀 값이 안 보임 (2026-07-12) -->
          <el-select v-model="filters.status" clearable placeholder="전체" style="width: 130px" @change="fetchData">
            <el-option label="예정" value="PLANNED" />
            <el-option label="부킹완료" value="BOOKED" />
            <el-option label="선적완료" value="LOADED" />
            <el-option label="운송중" value="IN_TRANSIT" />
            <el-option label="도착" value="ARRIVED" />
            <el-option label="인도완료" value="DELIVERED" />
            <el-option label="취소" value="CANCELLED" />
          </el-select>
        </el-form-item>
        <el-form-item label="법인">
          <el-select v-model="filters.company" clearable placeholder="전체" style="width: 140px" @change="fetchData">
            <el-option v-for="c in codeStore.ourCompanys" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="운송중만">
          <el-switch v-model="filters.inTransit" />
        </el-form-item>
        <el-form-item label="지연만">
          <el-switch v-model="filters.delayed" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="fetchData">검색</el-button>
          <el-button @click="resetFilters">초기화</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 데이터 테이블 -->
    <el-card class="table-card">
      <el-table :data="shipments || []" v-loading="loading" stripe>
        <el-table-column prop="shipment_number" label="선적번호" width="140" />
        <el-table-column prop="company_name" label="법인" width="100" />
        <el-table-column label="유형" width="80">
          <template #default="{ row }">
            <el-tag size="small">{{ row.shipment_type_display }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="상태" width="100">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ row.status_display }}
            </el-tag>
            <el-tag v-if="row.is_delayed" type="danger" size="small" style="margin-left: 4px">
              지연
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="main_request_number" label="메인오더" width="140" />
        <el-table-column prop="lc_number" label="LC번호" width="130" />
        <el-table-column prop="quantity" label="수량" width="80" align="right">
          <template #default="{ row }">
            {{ formatNumber(row.quantity) }}
          </template>
        </el-table-column>
        <el-table-column prop="carrier" label="선사" width="100" />
        <el-table-column prop="vessel_name" label="선박명" width="120" />
        <el-table-column prop="bl_number" label="B/L번호" width="120" />
        <el-table-column prop="port_of_loading" label="선적항" width="80" />
        <el-table-column prop="port_of_discharge" label="양륙항" width="80" />
        <el-table-column prop="etd" label="ETD" width="100" />
        <el-table-column prop="eta" label="ETA" width="100" />
        <el-table-column label="도착까지" width="80" align="center">
          <template #default="{ row }">
            <el-tag
              v-if="row.days_until_eta !== null && row.days_until_eta !== undefined"
              :type="row.days_until_eta < 0 ? 'danger' : row.days_until_eta <= 3 ? 'warning' : 'info'"
              size="small"
            >
              {{ row.days_until_eta < 0 ? `D+${Math.abs(row.days_until_eta)}` : `D-${row.days_until_eta}` }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="작업" width="250" fixed="right">
          <template #default="{ row }">
            <el-dropdown
              v-if="userStore.isLCManager"
              @command="(cmd) => handleAction(cmd, row)"
              style="margin-right: 8px"
            >
              <el-button size="small" type="primary">
                상태변경<el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item v-if="row.status === 'PLANNED'" command="book">
                    부킹 확정
                  </el-dropdown-item>
                  <el-dropdown-item v-if="['PLANNED', 'BOOKED'].includes(row.status)" command="load">
                    선적 완료
                  </el-dropdown-item>
                  <el-dropdown-item v-if="['LOADED', 'IN_TRANSIT'].includes(row.status)" command="arrive">
                    도착 처리
                  </el-dropdown-item>
                  <el-dropdown-item v-if="row.status === 'ARRIVED'" command="deliver">
                    인도 완료
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            <el-button
              v-if="userStore.isLCManager"
              size="small"
              @click="openEditDialog(row)"
            >수정</el-button>
            <el-button size="small" @click="openDetailDialog(row)">상세</el-button>
            <!-- 자재 대사 Phase B (2026-08-02) — 차수 취합 드로어 -->
            <el-button size="small" type="success" plain @click="openMaterialDrawer(row)">자재</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next"
        @size-change="fetchData"
        @current-change="fetchData"
      />
    </el-card>

    <!-- 생성/수정 다이얼로그 (2026-07-12: 수정 모드 추가) -->
    <ShipmentFormDialog
      v-model="showCreateDialog"
      :shipment="editingShipment"
      @saved="handleSaved"
    />

    <!-- 상세 다이얼로그 -->
    <ShipmentDetailDialog
      v-model="showDetailDialog"
      :shipment="selectedShipment"
    />

    <!-- 도움말 패널 -->
    <HelpPanel
      v-model="helpVisible"
      :content-path="'screens/shipment-list'"
    />

    <!-- ===== 차수 취합 드로어 (2026-08-02, 자재 대사 Phase B —
         docs/material-reconciliation-design.md §3-C ②③) ===== -->
    <el-drawer v-model="drawerVisible" size="720px" :with-header="false">
      <div v-if="matSummary" class="mat-drawer" v-loading="drawerLoading">
        <div class="mat-drawer-header">
          <div>
            <!-- [결정②] 차수 표시명 = 실무 호칭 "M월 D일 20FT" -->
            <h3>{{ matSummary.label || matSummary.shipment_number }}</h3>
            <span class="mat-sub">
              {{ matSummary.shipment_number }} ·
              <el-tag :type="getStatusType(matSummary.status)" size="small">
                {{ matSummary.status_display }}
              </el-tag>
              ETD {{ matSummary.etd || '-' }} / ETA {{ matSummary.eta || '-' }}
            </span>
          </div>
          <div class="mat-actions">
            <!-- [결정④] 브랜드 필터 출력 = 팀장별 파일 관행 흡수 -->
            <el-select v-model="exportBrand" clearable placeholder="전체 브랜드"
              size="small" style="width: 130px">
              <el-option v-for="b in drawerBrands" :key="b.id"
                :label="b.name" :value="b.id" />
            </el-select>
            <el-button size="small" @click="downloadActual" :loading="exporting">
              액츄얼 출력
            </el-button>
            <el-button type="primary" size="small" @click="openLoadDialog">자재 실기</el-button>
          </div>
        </div>

        <h4>스타일별 적재</h4>
        <el-table :data="matSummary.styles" border size="small" stripe>
          <el-table-column prop="request_number" label="메인오더" width="130" />
          <el-table-column prop="brand_name" label="브랜드" width="90" />
          <el-table-column prop="style_number" label="스타일" width="110" />
          <el-table-column prop="item_count" label="자재" width="60" align="right" />
          <el-table-column prop="quantity_sum" label="수량 합" width="90" align="right" />
          <el-table-column label="잔여 미출고" width="95" align="center">
            <template #default="{ row }">
              <el-tag v-if="row.unshipped_count > 0" type="warning" size="small">
                {{ row.unshipped_count }}건
              </el-tag>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>

        <h4>패킹리스트</h4>
        <el-table :data="matSummary.packing_lists" border size="small" stripe>
          <el-table-column prop="packing_list_number" label="번호" width="150" />
          <el-table-column label="상태" width="80" align="center">
            <template #default="{ row }">
              <el-tag size="small"
                :type="row.status === 'DRAFT' ? 'info' : 'success'">
                {{ row.status_display }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="request_number" label="메인오더" width="130" />
          <el-table-column prop="item_count" label="항목" width="60" align="right" />
          <el-table-column prop="quantity_sum" label="수량" width="90" align="right" />
          <el-table-column prop="total_gross_weight" label="GW" width="80" align="right" />
        </el-table>
        <div class="mat-hint">
          작성중(DRAFT) 패킹리스트는 패킹 화면에서 수량 확인 후 확정하세요 —
          확정분만 자재 대사의 출고량에 집계됩니다.
        </div>
      </div>
    </el-drawer>

    <!-- [자재 실기] 다이얼로그 — 미출고 잔량을 이 차수에 담기 -->
    <el-dialog v-model="loadDialogVisible" title="자재 실기 — 미출고 잔량" width="860px">
      <div class="load-filters">
        <el-input v-model="loadFilters.q" placeholder="품명 검색" clearable
          style="width: 160px" @change="fetchUnshipped" />
        <el-select v-model="loadFilters.material_type" clearable placeholder="유형"
          style="width: 100px" @change="fetchUnshipped">
          <el-option label="원자재" value="RAW" />
          <el-option label="부자재" value="ACC" />
        </el-select>
        <el-checkbox v-model="loadFilters.availableOnly" @change="fetchUnshipped">
          실을 재고 있는 것만
        </el-checkbox>
        <span class="mat-hint">체크한 자재의 잔량으로 스타일별 패킹리스트 초안을 만듭니다</span>
      </div>
      <el-table :data="unshippedRows" border size="small" stripe height="420"
        v-loading="unshippedLoading"
        @selection-change="(rows) => (selUnshipped = rows)">
        <el-table-column type="selection" width="40" />
        <el-table-column prop="request_number" label="메인오더" width="120" />
        <el-table-column prop="brand_name" label="브랜드" width="80" />
        <el-table-column prop="item_name" label="품명" min-width="110" show-overflow-tooltip />
        <el-table-column prop="specification" label="규격" width="80" show-overflow-tooltip />
        <el-table-column prop="color" label="색상" width="70" />
        <el-table-column label="소요" width="80" align="right">
          <template #default="{ row }">{{ fmtQty(row.required_quantity) }}</template>
        </el-table-column>
        <el-table-column label="보낼 잔량" width="90" align="right">
          <template #default="{ row }">{{ fmtQty(row.to_ship) }}</template>
        </el-table-column>
        <el-table-column label="실을 재고" width="90" align="right">
          <template #default="{ row }">
            <span :class="{ 'no-stock': parseFloat(row.available) <= 0 }">
              {{ fmtQty(row.available) }} {{ row.unit }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="supplier_name" label="공급업체" width="100" show-overflow-tooltip />
      </el-table>
      <template #footer>
        <el-button @click="loadDialogVisible = false">취소</el-button>
        <el-button type="primary" :loading="loadingMaterials"
          :disabled="!selUnshipped.length" @click="submitLoadMaterials">
          선택 {{ selUnshipped.length }}건 싣기
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { Plus, ArrowDown, QuestionFilled } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useCodeStore } from '@/stores/codeStore'
import ShipmentFormDialog from '@/components/trade/ShipmentFormDialog.vue'
import ShipmentDetailDialog from '@/components/trade/ShipmentDetailDialog.vue'
import HelpPanel from '@/components/help/HelpPanel.vue'

const userStore = useUserStore()
const codeStore = useCodeStore()

const loading = ref(false)
const shipments = ref([])
const selectedShipment = ref(null)
const helpVisible = ref(false)
const showCreateDialog = ref(false)
const showDetailDialog = ref(false)

const filters = reactive({
  status: null,
  company: null,
  inTransit: false,
  delayed: false
})

const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const getStatusType = (status) => {
  const types = {
    'PLANNED': 'info',
    'BOOKED': 'warning',
    'LOADED': '',
    'IN_TRANSIT': '',
    'ARRIVED': 'success',
    'DELIVERED': 'success',
    'CANCELLED': 'danger'
  }
  return types[status] || 'info'
}

const formatNumber = (num) => {
  return num?.toLocaleString() || '0'
}

const fetchData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      page_size: pagination.pageSize,
    }
    if (filters.status) params.status = filters.status
    if (filters.company) params.company = filters.company
    if (filters.inTransit) params.in_transit = 'true'
    if (filters.delayed) params.delayed = 'true'

    const response = await axiosInstance.get('shipments/', { params })
    shipments.value = response.data.results || response.data
    pagination.total = response.data.count || shipments.value.length
  } catch (error) {
    console.error('Failed to fetch shipments:', error)
  } finally {
    loading.value = false
  }
}

const resetFilters = () => {
  filters.status = null
  filters.company = null
  filters.inTransit = false
  filters.delayed = false
  pagination.page = 1
  fetchData()
}

const handleAction = async (action, shipment) => {
  const messages = {
    'book': '부킹 확정 처리하시겠습니까?',
    'load': '선적 완료 처리하시겠습니까?',
    'arrive': '도착 처리하시겠습니까?',
    'deliver': '인도 완료 처리하시겠습니까?'
  }

  try {
    await ElMessageBox.confirm(messages[action], '확인')

    await axiosInstance.post(`shipments/${shipment.id}/${action}/`)
    ElMessage.success('상태가 변경되었습니다.')
    fetchData()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('상태 변경에 실패했습니다.')
    }
  }
}

const openCreateDialog = () => {
  editingShipment.value = null
  showCreateDialog.value = true
}

// 수정 (2026-07-12)
const editingShipment = ref(null)
const openEditDialog = (row) => {
  editingShipment.value = row
  showCreateDialog.value = true
}

const openDetailDialog = (shipment) => {
  selectedShipment.value = shipment
  showDetailDialog.value = true
}

const handleSaved = () => {
  showCreateDialog.value = false
  fetchData()
}

const showHelp = () => {
  helpVisible.value = true
}

// ===== 차수 취합 드로어 + [자재 실기] (2026-08-02, 자재 대사 Phase B) =====
const drawerVisible = ref(false)
const drawerLoading = ref(false)
const matSummary = ref(null)
const drawerShipmentId = ref(null)

const openMaterialDrawer = async (row) => {
  drawerShipmentId.value = row.id
  drawerVisible.value = true
  await fetchMatSummary()
}

const fetchMatSummary = async () => {
  if (!drawerShipmentId.value) return
  drawerLoading.value = true
  try {
    const res = await axiosInstance.get(
      `shipments/${drawerShipmentId.value}/material-summary/`)
    matSummary.value = res.data
  } catch (e) {
    ElMessage.error('차수 자재 요약 조회에 실패했습니다.')
  } finally {
    drawerLoading.value = false
  }
}

const loadDialogVisible = ref(false)
const unshippedRows = ref([])
const unshippedLoading = ref(false)
const selUnshipped = ref([])
const loadingMaterials = ref(false)
const loadFilters = reactive({ q: '', material_type: null, availableOnly: true })

const openLoadDialog = () => {
  selUnshipped.value = []
  loadDialogVisible.value = true
  fetchUnshipped()
}

const fetchUnshipped = async () => {
  unshippedLoading.value = true
  try {
    const params = {}
    if (loadFilters.q) params.q = loadFilters.q
    if (loadFilters.material_type) params.material_type = loadFilters.material_type
    if (loadFilters.availableOnly) params.available_only = 1
    const res = await axiosInstance.get(
      'production/main-request-materials/unshipped/', { params })
    unshippedRows.value = res.data.rows || []
  } catch (e) {
    ElMessage.error('미출고 목록 조회에 실패했습니다.')
  } finally {
    unshippedLoading.value = false
  }
}

const submitLoadMaterials = async () => {
  loadingMaterials.value = true
  try {
    const res = await axiosInstance.post(
      `shipments/${drawerShipmentId.value}/load-materials/`,
      { material_ids: selUnshipped.value.map(r => r.id) })
    ElMessage.success(res.data.message)
    if (res.data.skipped?.length) {
      ElMessage.warning(`건너뜀: ${res.data.skipped.join(', ')}`)
    }
    loadDialogVisible.value = false
    await fetchMatSummary()
  } catch (e) {
    ElMessage.error(e.response?.data?.error || '자재 실기에 실패했습니다.')
  } finally {
    loadingMaterials.value = false
  }
}

const fmtQty = (v) => {
  if (v === null || v === undefined) return '-'
  const n = parseFloat(v)
  return n % 1 === 0 ? n.toLocaleString() : n.toFixed(2)
}

// [액츄얼 출력] — 차수 통합 엑셀 (2026-08-02, Phase C)
const exportBrand = ref(null)
const exporting = ref(false)
const drawerBrands = computed(() => {
  const seen = new Map()
  for (const s of matSummary.value?.styles || []) {
    if (s.brand_id && !seen.has(s.brand_id)) {
      seen.set(s.brand_id, { id: s.brand_id, name: s.brand_name })
    }
  }
  return [...seen.values()]
})

const downloadActual = async () => {
  exporting.value = true
  try {
    const params = {}
    if (exportBrand.value) params.brand = exportBrand.value
    const res = await axiosInstance.get(
      `shipments/${drawerShipmentId.value}/actual-export/`,
      { params, responseType: 'blob' })
    const disposition = res.headers['content-disposition'] || ''
    const m = disposition.match(/filename\*=UTF-8''(.+)/)
    const filename = m ? decodeURIComponent(m[1]) : '액츄얼.xlsx'
    const url = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    ElMessage.error('액츄얼 출력에 실패했습니다.')
  } finally {
    exporting.value = false
  }
}

onMounted(() => {
  fetchData()
  codeStore.fetchOurCompanys()
})
</script>

<style scoped>
.shipment-list-view {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.filter-card {
  margin-bottom: 20px;
}

.table-card {
  margin-bottom: 20px;
}

.el-pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

/* 차수 취합 드로어 (2026-08-02) */
.mat-drawer {
  padding: 4px 8px;
}
.mat-drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}
.mat-drawer-header h3 {
  margin: 0 0 4px;
}
.mat-actions {
  display: flex;
  gap: 6px;
  align-items: center;
}
.mat-sub {
  font-size: 12px;
  color: #909399;
}
.mat-drawer h4 {
  margin: 14px 0 6px;
  font-size: 13px;
}
.mat-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
}
.load-filters {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}
.no-stock {
  color: #c0c4cc;
}
</style>
